/**
 * Cc field — a searchable multi-value email input (Kai: "every dropdown is a
 * searchable dropdown like this", pointing at Freshservice's Cc "Type to
 * search"). Type to filter org people; Enter/comma also accepts a raw email
 * for someone outside the org — a Cc list is not limited to known contacts.
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import type { Person } from './api';
import { NO_AUTOFILL } from '../../components/ui/input';

export function EmailChipInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  // URL state on purpose: typeahead buffer inside a chip input; the chosen
  // addresses are the state that matters.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Person[]>([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const res = await apiFetch<{ entries: Person[] }>(
          `/api/org/people?q=${encodeURIComponent(query.trim())}`,
        );
        setResults(res.entries.filter((p) => !value.includes(p.email)));
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, value]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function add(email: string) {
    const clean = email.trim().toLowerCase();
    if (clean && !value.includes(clean)) onChange([...value, clean]);
    setQuery('');
    setResults([]);
    setOpen(false);
  }
  function remove(email: string) {
    onChange(value.filter((v) => v !== email));
  }

  return (
    <div ref={rootRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring">
        {value.map((email) => (
          <span key={email} className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs">
            {email}
            <button
              type="button"
              aria-label={`Remove ${email}`}
              onClick={() => remove(email)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {/* `role="combobox"` says what this actually IS — a custom widget,
            not a form field. Correct ARIA regardless, and it is also part of
            what stops the browser classifying it as an address field and
            drawing its own autofill list over ours. */}
        <input
          {...NO_AUTOFILL}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ',') && query.trim()) {
              e.preventDefault();
              add(query);
            } else if (e.key === 'Backspace' && !query && value.length) {
              remove(value[value.length - 1]!);
            }
          }}
          placeholder={value.length ? '' : 'Type to search…'}
          className="min-w-[8rem] flex-1 border-0 bg-transparent p-0.5 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              className={cn(
                'flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-accent',
              )}
              onClick={() => add(p.email)}
            >
              <span className="truncate">{p.name ?? p.email}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{p.name ? p.email : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
